/**
 * Ticket Z14: Anfrageseite /100-tage/weiterbildung.
 *
 * Zeigt die für die öffentliche Seite freigegebenen Kurse und Trainings aus dem
 * Leistungskatalog und nimmt unverbindliche Anfragen auf. Anfragen gehen per
 * Mail an hello@creategy.de und in die gemeinsame Anfragenliste im Backend.
 */

import { createFileRoute } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { TrainingInquiryForm } from "@/components/landing/TrainingInquiryForm";
import {
  getLandingContent,
  getTrainingOffers,
} from "@/lib/landing-hundert-tage.functions";

const TITLE = "Kurse und Trainings anfragen | creategy";
const DESCRIPTION =
  "Unverbindlich Kurse und Trainings rund um die ersten 100 Tage im Amt anfragen: Auswahl treffen, Angaben senden, wir melden uns.";

export const Route = createFileRoute("/100-tage/weiterbildung/")({
  loader: async () => {
    const [content, offers] = await Promise.all([getLandingContent(), getTrainingOffers()]);
    return { content, offers };
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://creategy.de/100-tage/weiterbildung" }],
  }),
  component: WeiterbildungPage,
});

function WeiterbildungPage() {
  const { content, offers } = Route.useLoaderData();

  return (
    <Layout>
      {/* Sektion 1: Einordnung */}
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <p className="font-display text-sm font-extrabold uppercase tracking-wide text-brand-wahlen">
            100 Tage im Amt
          </p>
          <h1 className="heading-h1 mt-4 text-primary">Kurse und Trainings anfragen</h1>
          <div className="markdown-body mt-8 max-w-[68ch]">
            {content.trainingNoteMd ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.trainingNoteMd}</ReactMarkdown>
            ) : (
              <p>
                Wählen Sie aus, was für Sie interessant ist. Sie erhalten von uns ein passendes
                Angebot. Die Anfrage ist unverbindlich und kostet nichts.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Sektion 2: Auswahl und Formular */}
      <section aria-labelledby="weiterbildung-formular">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="weiterbildung-formular" className="heading-h2 text-primary">
              Ihre Anfrage
            </h2>
            <div className="mt-10 max-w-3xl">
              <TrainingInquiryForm offers={offers} />
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
